import { useField, useFormikContext } from 'formik';
import { memo, useRef, useCallback, useEffect } from 'react';
import { ErrorLevel } from '../../../../API/Enums';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { GMTextField } from '..';
import { FormTextFieldProps } from './FormTextField.types';
import { TextFieldProps } from '../TextField/TextField.types';

/**
 * Textfield for use in a Formik.
 *
 * Sets the value, onChange, error, & helperText internally
 */
const FormTextField: GoMotoComponent<FormTextFieldProps> = memo(({
    validateOnChange,
    validateBeforeSubmit,
    onBlur: propsOnBlur,
    onChange: propsOnChange,
    helperText,
    warningText,
    ...props
}) => {
    const [field, meta] = useField(props.name);
    const { setFieldValue, validateField, setFieldTouched, submitCount } = useFormikContext();
    const prevValueRef = useRef<string>(field.value);
    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (propsOnChange) {
            propsOnChange(e);
        }
        else {
            setFieldValue(props.name, e.target.value);
        }
    }, [props.name, propsOnChange, setFieldValue]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setFieldTouched(props.name, true);
        if (propsOnBlur) {
            propsOnBlur(e);
        }
    }, [propsOnBlur, setFieldTouched, props.name]);

    useEffect(() => {
        if (validateOnChange && meta.touched && field.value !== prevValueRef.current) {
            validateField(props.name);
        }

        prevValueRef.current = field.value;
    }, [field.value, meta.touched, props.name, validateOnChange, validateField]);

    const getErrorLevel = (): ErrorLevel => {
        if (!!meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return ErrorLevel.Error;
        }

        if (!!warningText) {
            return ErrorLevel.Warning;
        }

        return ErrorLevel.None;
    };

    const getHelperText = (): TextFieldProps['helperText'] => {
        if (meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return meta.error;
        }

        if (warningText) {
            return warningText;
        }

        return helperText;
    };

    return (
        <GMTextField
            value={field.value ?? ''}
            onChange={onChange}
            error={getErrorLevel()}
            helperText={getHelperText()}
            onBlur={onBlur}
            {...props}
        />
    );
});

export default FormTextField;